import { useEffect, useRef, useState } from 'react';
import {
  ALLOWED_FONTS,
  MENU_STYLES,
  THEME_MODES,
  resolveCustomerFeatures,
  type PublicBranding,
} from '@busbook/shared';

/**
 * Live-preview channel for the branding editor (docs/04 §3).
 *
 * The admin frames this app and posts draft branding to it. Two guards keep that
 * from becoming a way for any site to repaint a live tenant:
 *   1. messages are only accepted while this app is inside an iframe, and
 *   2. the payload is validated field by field — nothing but theme values is read.
 * No data is ever sent back, and the draft is never persisted here.
 *
 * The draft is a PATCH over the tenant's published branding, not a replacement. The
 * editor that posts it owns identity — name, logo, colours, type, light or dark — and
 * nothing else: navigation shape and the customer feature set are the platform's to set
 * (DECISIONS D69), so a console that no longer edits them must not have to restate them
 * to get a preview. Anything the draft omits is taken from what is published, which is
 * also what makes the frame an honest preview rather than a differently-shaped app.
 */

const PREVIEW_MESSAGE = 'busbook:preview-branding';

function isHex(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
}

/**
 * Accepts only a well-formed identity payload; anything odd is ignored entirely.
 *
 * `published` is the branding this tenant actually serves, and every field the draft does
 * not carry is taken from it. Identity fields are still required and still validated —
 * a half-written draft must not repaint the frame — but the navigation fields are
 * optional, because the console that sends them no longer edits them.
 */
export function parsePreviewBranding(
  input: unknown,
  published: PublicBranding,
): PublicBranding | null {
  if (!input || typeof input !== 'object') return null;
  const draft = input as Record<string, unknown>;
  const colors = draft.colors as Record<string, unknown> | undefined;
  if (!colors) return null;

  for (const key of ['primary', 'secondary', 'background', 'surface', 'text']) {
    if (!isHex(colors[key])) return null;
  }
  if (typeof draft.appName !== 'string' || draft.appName.length === 0 || draft.appName.length > 40) {
    return null;
  }
  if (!(ALLOWED_FONTS as readonly string[]).includes(String(draft.fontFamily))) return null;
  if (!(THEME_MODES as readonly string[]).includes(String(draft.themeMode))) return null;

  // Absent => keep what is published. Present but not a menu style we know => the whole
  // message is rejected, same as a malformed colour: a draft that says something we
  // cannot read is a bug worth surfacing, not a field to quietly ignore.
  let menuStyle = published.menuStyle;
  if (draft.menuStyle !== undefined) {
    if (!(MENU_STYLES as readonly string[]).includes(String(draft.menuStyle))) return null;
    menuStyle = draft.menuStyle as PublicBranding['menuStyle'];
  }

  let menuItems = published.menuItems;
  if (draft.menuItems !== undefined) {
    if (!Array.isArray(draft.menuItems)) return null;
    menuItems = draft.menuItems
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((item) => ({
        key: String(item.key),
        label: String(item.label ?? ''),
        visible: item.visible !== false,
      }))
      .filter((item) => item.label.length > 0 && item.label.length <= 24) as
      PublicBranding['menuItems'];
  }

  return {
    appName: draft.appName,
    logoUrl: typeof draft.logoUrl === 'string' ? draft.logoUrl : null,
    iconUrl: typeof draft.iconUrl === 'string' ? draft.iconUrl : null,
    colors: {
      primary: colors.primary as string,
      secondary: colors.secondary as string,
      background: colors.background as string,
      surface: colors.surface as string,
      text: colors.text as string,
    },
    fontFamily: draft.fontFamily as string,
    menuStyle,
    menuItems,
    themeMode: draft.themeMode as PublicBranding['themeMode'],
    // Resolved through the same reader the API uses, so the preview shows what a
    // passenger would actually get. A draft that says nothing about features keeps the
    // published set rather than expanding to the all-on default, which would show the
    // operator capabilities their plan does not include.
    customerFeatures:
      draft.customerFeatures === undefined
        ? published.customerFeatures
        : resolveCustomerFeatures(draft.customerFeatures),
  };
}

const isFramed = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws, which itself means we are framed.
    return true;
  }
};

/**
 * Returns draft branding pushed by the admin preview, or null in normal use.
 *
 * `published` is what this tenant serves today; the draft is merged over it. Null while
 * the tenant context is still loading, which is also when no preview can be applied.
 */
export function usePreviewBranding(published: PublicBranding | null): PublicBranding | null {
  const [preview, setPreview] = useState<PublicBranding | null>(null);

  // Read through a ref so a new published branding does not tear down the listener and
  // lose a draft that has already been posted — the admin only re-posts on a change.
  const base = useRef(published);
  base.current = published;

  useEffect(() => {
    if (!isFramed()) return;

    function onMessage(event: MessageEvent) {
      const data = event.data as { type?: string; branding?: unknown } | null;
      if (!data || data.type !== PREVIEW_MESSAGE) return;
      if (!base.current) return;
      const parsed = parsePreviewBranding(data.branding, base.current);
      if (parsed) setPreview(parsed);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return preview;
}
